import { useEffect, useId, useRef, useState } from 'react'
import { Check, ChevronDown } from 'lucide-react'

/**
 * A pill that opens a short list — the "sweet dropdown" the owner asked for
 * (2026-09-11) for the two day-scoped answers on /today: how much you are up
 * for, and how today felt.
 *
 * Why a pill and not a native <select>: each option carries a second line
 * (the units a rung grants), the list needs a title that IS the question, and
 * a footnote that says what choosing does. A native select can show none of
 * those. Why not the five-button block it replaces: five 44px buttons with
 * two lines each is a form, and it sat above the fold of a page whose first
 * job is to lower the heart rate.
 *
 * Rules kept:
 *   - the pill READS as the current answer ("Up for · Average"), so the page
 *     states a fact even when the list is closed;
 *   - the list says what the choice does, in words the server gave it — this
 *     component never invents a consequence;
 *   - Escape, outside click and choosing all close it; the trigger keeps focus.
 */

export interface PillOption {
  value: string
  label: string
  /** One quiet line under the label — e.g. "2 core · 1 optional". */
  detail?: string
}

export interface PillSelectProps {
  /** Two or three words before the value: "Up for". Rendered muted. */
  prefix?: string
  /** The current answer, as the pill shows it. */
  value: string
  /** True when nothing has been chosen and `value` is the default in force —
   *  the pill then reads quieter, so a default never looks like a choice. */
  unset?: boolean
  icon?: React.ReactNode
  /** The question. Also the pill's accessible name. */
  question: string
  /** One sentence under the question saying what answering does. */
  hint?: string
  options: PillOption[]
  selected: string | null
  onSelect: (value: string) => void | Promise<void>
  /** A closing line — "Today only. Back to Average tomorrow." */
  footnote?: string | null
  busy?: boolean
  tone?: 'brand' | 'ok'
  className?: string
}

const TONE = {
  brand: {
    pill: 'border-brand-subtle-border bg-brand-subtle text-brand',
    picked: 'text-brand',
  },
  ok: {
    pill: 'border-tint-ok-edge bg-tint-ok-soft text-ok',
    picked: 'text-ok',
  },
} as const

export function PillSelect({
  prefix, value, unset = false, icon, question, hint, options, selected, onSelect,
  footnote, busy = false, tone = 'brand', className = '',
}: PillSelectProps) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const listId = useId()

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOpen(false); triggerRef.current?.focus() }
    }
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onDown)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onDown)
    }
  }, [open])

  const t = TONE[tone]
  const pillTone = unset
    ? 'border-soft bg-surface-alt text-secondary'
    : t.pill

  return (
    <div ref={rootRef} className={`relative inline-block ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={`${question} ${prefix ? `${prefix} ` : ''}${value}`}
        title={question}
        disabled={busy}
        onClick={() => setOpen(v => !v)}
        className={`inline-flex min-h-touch items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors hover:brightness-95 disabled:opacity-60 ${pillTone}`}
      >
        {icon && <span className="shrink-0" aria-hidden="true">{icon}</span>}
        {prefix && <span className="font-normal opacity-80">{prefix}</span>}
        {prefix && <span className="opacity-50" aria-hidden="true">·</span>}
        <span className="font-semibold">{value}</span>
        <ChevronDown className={`h-3.5 w-3.5 shrink-0 opacity-70 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>

      {open && (
        <div
          className="popover-fit absolute left-0 top-full z-30 mt-1.5 w-72 rounded-2xl border border-soft bg-surface p-2 shadow-lg"
        >
          <div className="px-2 pb-2 pt-1">
            <p className="text-app-body font-semibold text-primary">{question}</p>
            {hint && <p className="mt-0.5 text-app-micro text-muted">{hint}</p>}
          </div>
          <ul id={listId} role="listbox" aria-label={question} className="space-y-0.5">
            {options.map(o => {
              const picked = o.value === selected
              return (
                <li key={o.value} role="option" aria-selected={picked}>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={async () => {
                      setOpen(false)
                      triggerRef.current?.focus()
                      await onSelect(o.value)
                    }}
                    className={`flex w-full min-h-touch items-center gap-3 rounded-xl px-3 py-2 text-left transition-colors hover:bg-surface-alt disabled:opacity-60 ${picked ? 'bg-surface-alt' : ''}`}
                  >
                    <span className="min-w-0 flex-1">
                      <span className={`block text-app-body font-medium ${picked ? t.picked : 'text-primary'}`}>{o.label}</span>
                      {o.detail && <span className="block text-app-micro text-muted">{o.detail}</span>}
                    </span>
                    {picked && <Check className={`h-4 w-4 shrink-0 ${t.picked}`} aria-hidden="true" />}
                  </button>
                </li>
              )
            })}
          </ul>
          {footnote && (
            <p className="px-2 pb-1 pt-2 text-app-micro text-muted">{footnote}</p>
          )}
        </div>
      )}
    </div>
  )
}

export default PillSelect
